import React from 'react';
import CodeBlock from '../../components/common/CodeBlock';
import AuthorInfo from '../../components/common/AuthorInfo';
import { SectionHeading, SubHeading, Para, Mark, Code, Callout, Output, Pill, TwoUp, QA } from './articleKit';

type Author = { name: string; fullName: string; avatar?: string };

export const JSPrototypes: React.FC<{ author: Author }> = ({ author }) => (
 <div className="max-w-none">
  {/* Hero header */}
  <div className="mb-10 pb-6 border-b border-gray-200 dark:border-gray-700">
   <h1 className="text-4xl font-bold tracking-tight text-gray-900 dark:text-white mb-4">Prototypes &amp; Inheritance</h1>
   <p className="text-lg leading-8 text-gray-600 dark:text-gray-400 mb-5">
    JavaScript is fundamentally prototype-based. Objects share behavior through the prototype chain — and ES6 classes are just cleaner syntax over the same mechanism.
   </p>
   <AuthorInfo name={author.name} fullName={author.fullName} avatar={author.avatar} lastUpdated="June 2026" readTime="20 min read" />
  </div>

  {/* 1. The Real Problem */}
  <section className="mb-12">
   <SectionHeading n={1}>The Real Problem</SectionHeading>
   <Para>Imagine a food delivery platform that creates 10,000 delivery-partner objects:</Para>
   <CodeBlock snippets={[{ language: 'javascript', code: `const rider1 = { name: "Rider1", deliver() { console.log("Delivering"); } };\nconst rider2 = { name: "Rider2", deliver() { console.log("Delivering"); } };\nconst rider3 = { name: "Rider3", deliver() { console.log("Delivering"); } };` }]} defaultLanguage="javascript" id="proto-problem" />
   <Para>Harmless at three. But at <Mark>10,000 objects</Mark>, every single one stores the same <Code>deliver()</Code> function. Why store identical behavior 10,000 times? It&apos;s a huge waste of memory. JavaScript needed a way to <Mark>share behavior across objects</Mark> — that mechanism is the <Mark>prototype</Mark>.</Para>
  </section>

  {/* 2. Why This Problem Exists */}
  <section className="mb-12">
   <SectionHeading n={2}>Why This Problem Exists</SectionHeading>
   <Para>Objects routinely share behavior. A <Code>User</Code>, <Code>Admin</Code>, <Code>Customer</Code>, <Code>Vendor</Code>, and <Code>Delivery Partner</Code> might all need <Code>login</Code>, <Code>logout</Code>, and <Code>updateProfile</Code>.</Para>
   <Para>Should every object store duplicate copies? No. Store once, share everywhere. That is the core motivation behind prototypes.</Para>
  </section>

  {/* 3. Definition */}
  <section className="mb-12">
   <SectionHeading n={3}>Definition</SectionHeading>
   <div className="border-l-2 border-amber-500 bg-gray-50 dark:bg-gray-800/40 rounded-r-lg px-5 py-4 mb-4">
    <p className="text-[15px] leading-7 text-gray-800 dark:text-gray-200">A <Mark>prototype</Mark> is an object from which other objects can inherit properties and methods.</p>
   </div>
   <Para>Think of it as a <Mark>shared behavior object</Mark>. When JavaScript can&apos;t find a property on an object, it looks at that object&apos;s prototype.</Para>
  </section>

  {/* 4. First Example */}
  <section className="mb-12">
   <SectionHeading n={4}>First Example</SectionHeading>
   <CodeBlock snippets={[{ language: 'javascript', code: `const user = {\n  login() { console.log("Logged In"); },\n};\n\nconst admin = Object.create(user);\nadmin.login();` }]} defaultLanguage="javascript" id="proto-first" />
   <Output>{`Logged In`}</Output>
   <Para>Where is <Code>login()</Code>? Not inside <Code>admin</Code> — it comes from <Code>user</Code> through prototype inheritance.</Para>
  </section>

  {/* 5. Prototype Chain */}
  <section className="mb-12">
   <SectionHeading n={5}>The Prototype Chain</SectionHeading>
   <Para>Property lookup walks a chain of prototypes:</Para>
   <CodeBlock snippets={[{ language: 'javascript', code: `const grandParent = { country: "India" };\nconst parent = Object.create(grandParent);\nconst child = Object.create(parent);\n\nconsole.log(child.country);` }]} defaultLanguage="javascript" id="proto-chain" />
   <Output>{`India`}</Output>
   <CodeBlock snippets={[{ language: 'text', code: `child → parent → grandParent → (found "India")` }]} defaultLanguage="text" id="proto-chain-flow" />
   <Para>This lookup path is called the <Mark>prototype chain</Mark>.</Para>
  </section>

  {/* 6. Internal Lookup Algorithm */}
  <section className="mb-12">
   <SectionHeading n={6}>Internal Lookup Algorithm</SectionHeading>
   <Para>For <Code>child.country</Code>, JavaScript:</Para>
   <ol className="my-4 space-y-1.5 text-[14px] text-gray-700 dark:text-gray-300 list-decimal list-inside">
    <li>Checks <Code>child</Code> itself — not found.</li>
    <li>Checks <Code>child.__proto__</Code> — not found.</li>
    <li>Moves to the next prototype — found, return the value.</li>
   </ol>
   <CodeBlock snippets={[{ language: 'text', code: `Object → Prototype → Prototype → Prototype → null` }]} defaultLanguage="text" id="proto-lookup" />
   <Para>The chain ends at <Code>null</Code>. If nothing is found by then, the result is <Code>undefined</Code>.</Para>
  </section>

  {/* 7. Every Object Has A Prototype */}
  <section className="mb-12">
   <SectionHeading n={7}>Every Object Has a Prototype</SectionHeading>
   <CodeBlock snippets={[{ language: 'javascript', code: `const user = {};\nconsole.log(user.toString()); // works!` }]} defaultLanguage="javascript" id="proto-every" />
   <Para>Where does <Code>toString()</Code> come from? <Code>user</Code> never defined it — it&apos;s inherited from <Code>Object.prototype</Code>, which sits at the top of almost every chain.</Para>
  </section>

  {/* 8. __proto__ */}
  <section className="mb-12">
   <SectionHeading n={8}>Understanding __proto__</SectionHeading>
   <CodeBlock snippets={[{ language: 'javascript', code: `const user = { name: "Sachin" };\nconsole.log(user.__proto__); // Object.prototype` }]} defaultLanguage="javascript" id="proto-dunder" />
   <Para><Code>__proto__</Code> is the link to an object&apos;s parent (its prototype). Interviewers often ask how it differs from <Code>prototype</Code> — a common point of confusion.</Para>
  </section>

  {/* 9. prototype vs __proto__ */}
  <section className="mb-12">
   <SectionHeading n={9}>prototype vs __proto__</SectionHeading>
   <TwoUp
    left={{ title: 'prototype', items: ['Lives on functions', 'User.prototype', 'The object future instances inherit from'] }}
    right={{ title: '__proto__', items: ['Lives on objects (instances)', 'user.__proto__', 'The actual parent link used in lookup'] }}
   />
   <CodeBlock snippets={[{ language: 'javascript', code: `function User() {}\nconst user = new User();\n\nconsole.log(user.__proto__ === User.prototype); // true` }]} defaultLanguage="javascript" id="proto-vs" />
   <Para>That equality — <Code>user.__proto__ === User.prototype</Code> — is a very common interview question.</Para>
  </section>

  {/* 10. Constructor Functions */}
  <section className="mb-12">
   <SectionHeading n={10}>Constructor Functions</SectionHeading>
   <Para>Before ES6 classes, constructors built objects. Shared methods should live on the prototype, not inside the constructor:</Para>
   <CodeBlock snippets={[{ language: 'javascript', code: `function User(name) {\n  this.name = name;\n}\n\nUser.prototype.greet = function () {\n  console.log(this.name);\n};\n\nconst user = new User("Sachin");\nuser.greet(); // Sachin` }]} defaultLanguage="javascript" id="proto-ctor" />
   <Para>Now every instance shares the one <Code>greet</Code> method via <Code>User.prototype</Code>.</Para>
  </section>

  {/* 11. Memory Benefit */}
  <section className="mb-12">
   <SectionHeading n={11}>The Memory Benefit</SectionHeading>
   <TwoUp
    left={{ title: '❌ Method in constructor', items: ['this.greet = function () {}', 'Every object gets a NEW function', '10,000 objects = 10,000 functions'] }}
    right={{ title: '✅ Method on prototype', items: ['User.prototype.greet = ...', 'One function, shared by all', '10,000 objects = 1 function'] }}
   />
   <Para>Back to the 10,000 riders — putting <Code>deliver()</Code> on the prototype means one shared function instead of ten thousand copies. Huge memory savings.</Para>
  </section>

  {/* 12. Classical Inheritance Problem */}
  <section className="mb-12">
   <SectionHeading n={12}>The Classical Inheritance Problem</SectionHeading>
   <Para>Suppose <Code>Animal</Code> has <Code>eat()</Code>, and <Code>Dog</Code> should also have <Code>eat()</Code>. Copying it onto <Code>Dog</Code> duplicates code:</Para>
   <CodeBlock snippets={[{ language: 'javascript', code: `const dog = { eat() {} }; // duplicate of Animal.eat — we want reuse instead` }]} defaultLanguage="javascript" id="proto-classical" />
  </section>

  {/* 13. Prototype Inheritance Example */}
  <section className="mb-12">
   <SectionHeading n={13}>Prototype Inheritance Example</SectionHeading>
   <CodeBlock snippets={[{ language: 'javascript', code: `const animal = {\n  eat() { console.log("Eating"); },\n};\n\nconst dog = Object.create(animal);\ndog.eat(); // Eating (inherited)` }]} defaultLanguage="javascript" id="proto-inherit" />
   <Para>The method is inherited rather than duplicated.</Para>
  </section>

  {/* 14. Multi-Level Inheritance */}
  <section className="mb-12">
   <SectionHeading n={14}>Multi-Level Inheritance</SectionHeading>
   <CodeBlock snippets={[{ language: 'javascript', code: `const livingThing = { breathe() {} };\nconst animal = Object.create(livingThing);\nconst dog = Object.create(animal);` }]} defaultLanguage="javascript" id="proto-multi" />
   <CodeBlock snippets={[{ language: 'text', code: `dog → animal → livingThing` }]} defaultLanguage="text" id="proto-multi-flow" />
   <Para>Inheritance can span as many levels as you need.</Para>
  </section>

  {/* 15. ES6 Classes */}
  <section className="mb-12">
   <SectionHeading n={15}>ES6 Classes</SectionHeading>
   <CodeBlock snippets={[{ language: 'javascript', code: `class User {\n  constructor(name) {\n    this.name = name;\n  }\n  greet() {\n    console.log(this.name);\n  }\n}` }]} defaultLanguage="javascript" id="proto-class" />
   <Para>It looks different, but <Code>greet</Code> still lives on <Code>User.prototype</Code> internally.</Para>
  </section>

  {/* 16. Important Interview Truth */}
  <section className="mb-12">
   <SectionHeading n={16}>Important Interview Truth</SectionHeading>
   <Callout tone="warning" title="Classes did NOT add classical inheritance">
    Many developers think <Code>class</Code> introduced classical (Java-style) inheritance. It didn&apos;t. JavaScript is still <Mark>prototype-based</Mark> — classes are mostly <Mark>syntactic sugar</Mark> over prototypes.
   </Callout>
  </section>

  {/* 17. ES6 Inheritance */}
  <section className="mb-12">
   <SectionHeading n={17}>ES6 Inheritance with extends</SectionHeading>
   <CodeBlock snippets={[{ language: 'javascript', code: `class Animal {\n  eat() { console.log("Eating"); }\n}\n\nclass Dog extends Animal {}\n\nconst dog = new Dog();\ndog.eat(); // Eating` }]} defaultLanguage="javascript" id="proto-extends" />
   <Para>Under the hood, the prototype chain still powers everything — <Code>Dog.prototype</Code>&apos;s prototype is <Code>Animal.prototype</Code>.</Para>
  </section>

  {/* 18. Method Overriding */}
  <section className="mb-12">
   <SectionHeading n={18}>Method Overriding</SectionHeading>
   <CodeBlock snippets={[{ language: 'javascript', code: `class Animal {\n  speak() { console.log("Animal Sound"); }\n}\n\nclass Dog extends Animal {\n  speak() { console.log("Bark"); }\n}\n\nnew Dog().speak(); // Bark` }]} defaultLanguage="javascript" id="proto-override" />
   <Para>The child&apos;s method is found first in the chain, overriding the parent&apos;s implementation.</Para>
  </section>

  {/* 19. super Keyword */}
  <section className="mb-12">
   <SectionHeading n={19}>The super Keyword</SectionHeading>
   <CodeBlock snippets={[{ language: 'javascript', code: `class Animal {\n  speak() { console.log("Animal"); }\n}\n\nclass Dog extends Animal {\n  speak() {\n    super.speak(); // call the parent's version\n    console.log("Dog");\n  }\n}\n\nnew Dog().speak();` }]} defaultLanguage="javascript" id="proto-super" />
   <Output>{`Animal\nDog`}</Output>
   <Para><Code>super</Code> accesses the parent&apos;s methods (and constructor).</Para>
  </section>

  {/* 20. TypeScript Example */}
  <section className="mb-12">
   <SectionHeading n={20}>TypeScript Example</SectionHeading>
   <CodeBlock snippets={[{ language: 'typescript', code: `class User {\n  constructor(public name: string) {}\n  greet(): void {\n    console.log(this.name);\n  }\n}\n\nclass Admin extends User {\n  deleteUser(): void {\n    console.log("Deleted");\n  }\n}` }]} defaultLanguage="typescript" id="proto-ts" />
   <Para>TypeScript adds types and access modifiers, but the same prototype mechanism runs underneath.</Para>
  </section>

  {/* 21. Java Comparison */}
  <section className="mb-12">
   <SectionHeading n={21}>Java Comparison</SectionHeading>
   <TwoUp
    left={{ title: 'Java', items: ['Class-based inheritance', 'Class → Object', 'Fixed at compile time'] }}
    right={{ title: 'JavaScript', items: ['Prototype-based inheritance', 'Object → Prototype → Object', 'Dynamic at runtime'] }}
   />
   <Para>An important distinction: Java inherits from classes; JavaScript objects delegate to other objects via prototypes.</Para>
  </section>

  {/* 22. Common Interview Questions */}
  <section className="mb-12">
   <SectionHeading n={22}>Common Interview Questions</SectionHeading>
   <div className="flex flex-wrap gap-2">
    {['What is a prototype?', 'What is the prototype chain?', 'prototype vs __proto__?', 'How does property lookup work?', 'Are classes prototype-based?', 'How does extends work?', 'What is method overriding?', 'What does super do?', 'Why store methods on the prototype?'].map((q) => <Pill key={q}>{q}</Pill>)}
   </div>
   <div className="mt-5">
    <QA q="What is the difference between prototype and __proto__?">
     <Code>prototype</Code> is a property on <em>functions</em> — the object that instances created with <Code>new</Code> will inherit from. <Code>__proto__</Code> is the property on an <em>object</em> pointing to its actual parent. For an instance: <Code>user.__proto__ === User.prototype</Code>.
    </QA>
    <QA q="Are ES6 classes prototype-based?">
     Yes. <Code>class</Code> is syntactic sugar — methods still live on the prototype and inheritance still uses the prototype chain. JavaScript never gained true classical inheritance.
    </QA>
    <QA q="Why store methods on the prototype instead of the instance?">
     So all instances share one copy of each method instead of allocating a new function per object. With many instances this is a major memory saving and the reason prototypes exist.
    </QA>
   </div>
  </section>

  {/* 23. Common Production Mistakes */}
  <section className="mb-12">
   <SectionHeading n={23}>Common Production Mistakes</SectionHeading>
   <Callout tone="warning" title="Adding huge objects to a prototype">Bloats every instance&apos;s lookup and memory footprint.</Callout>
   <Callout tone="warning" title="Modifying built-in prototypes">
    <Code>Array.prototype.custom = ...</Code> is dangerous — it can collide with other code and future language features.
   </Callout>
   <Callout tone="warning" title="Deep prototype chains">Long chains hurt readability and slow property lookup.</Callout>
   <Callout tone="warning" title="Confusing class with prototype inheritance">Assuming classes behave like Java&apos;s — they don&apos;t.</Callout>
  </section>

  {/* 24. Mental Model */}
  <section className="mb-12">
   <SectionHeading n={24}>Mental Model</SectionHeading>
   <Para>Think of a company&apos;s employees. Every employee knows the company rules, but the rules are stored once and referenced by everyone — not copied into each person. Objects work the same way: they reference a shared prototype instead of duplicating its methods.</Para>
  </section>

  {/* 25. Interview One-Liner */}
  <section className="mb-12">
   <SectionHeading n={25}>Interview One-Liner</SectionHeading>
   <div className="border-l-2 border-amber-500 bg-gray-50 dark:bg-gray-800/40 rounded-r-lg px-5 py-4">
    <p className="text-[15px] leading-7 font-medium text-gray-800 dark:text-gray-200">
     A prototype is an object that lets other objects inherit shared properties and methods through the prototype chain, enabling memory-efficient behavior reuse.
    </p>
   </div>
  </section>

  {/* 26. Most Important Takeaway */}
  <section className="mb-4">
   <SectionHeading n={26}>Most Important Takeaway</SectionHeading>
   <Callout tone="info" title="Takeaway">
    JavaScript is fundamentally prototype-based. Objects inherit behavior through prototype chains. ES6 classes provide cleaner syntax but still rely entirely on prototypes underneath. Understanding prototypes explains inheritance, method sharing, property lookup, memory optimization, and many advanced JavaScript behaviors.
   </Callout>
  </section>
 </div>
);

export default JSPrototypes;
